import React, { useState } from "react";
import Loading from "../../components/Loading";
import { openXeroPdf, voidXeroDoc, refreshXero } from "../../hooks/dailyRecap/useXeroAccounting";

const fGBP = (v) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency", currency: "GBP", maximumFractionDigits: 0,
  }).format(v || 0);

// Backend sends YYYY-MM-DD; display as DD-MM-YYYY.
const fDate = (d) => {
  if (!d) return "";
  const [y, m, day] = d.split("-");
  return y && m && day ? `${day}-${m}-${y}` : d;
};

/** One Xero document row (PO or invoice). */
const DocRow = ({ doc, kind, flagLabel, onView, onVoidClick, busy, canVoid }) => (
  <div className="xero-row">
    <div className="xero-row__main">
      <div className="xero-row__top">
        <span className="xero-row__number">{doc.number || doc.reference || doc.xero_id}</span>
        <span className={`xero-status xero-status--${(doc.status || "").toLowerCase()}`}>{doc.status}</span>
        {doc.flagged && <span className="acct-tag acct-tag--warn">{flagLabel}</span>}
        {doc.is_duplicate && <span className="acct-tag acct-tag--dup">Duplicate</span>}
      </div>
      <div className="xero-row__meta">
        <span className="xero-row__product">{doc.product}</span>
        {doc.reference_number && <span className="acct-ref-tag">{doc.reference_number}</span>}
        {doc.contact && <span className="xero-row__contact">{doc.contact}</span>}
        {doc.date && <span className="xero-row__date">{fDate(doc.date)}</span>}
      </div>
    </div>
    <span className="xero-row__total">{fGBP(doc.total)}</span>
    <div className="xero-row__actions">
      <button type="button" className="xero-btn" onClick={() => onView(doc)}>PDF</button>
      {canVoid && (
        <button
          type="button"
          className="xero-btn xero-btn--danger"
          disabled={busy}
          onClick={() => onVoidClick(doc)}
        >
          Void
        </button>
      )}
    </div>
  </div>
);

/**
 * A Xero documents section (Purchase Orders or Invoices).
 * Props:
 *  - title, kind: "po" | "invoice", flagLabel (e.g. "No sell request")
 *  - data: { xero_connected, items: [...] }, isLoading, year, month, refetch
 */
const XeroDocsSection = ({ title, kind, flagLabel, data, isLoading, year, month, refetch, canVoid }) => {
  const [confirm, setConfirm] = useState(null); // the doc pending void
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const items = data?.items || [];
  const connected = data?.xero_connected !== false;
  const main = items.filter((d) => !d.is_duplicate);
  const dupes = items.filter((d) => d.is_duplicate);

  // Group duplicates by their shared key so each set of similar docs renders together.
  const dupeGroups = Object.values(
    dupes.reduce((acc, d) => {
      (acc[d.dup_key] = acc[d.dup_key] || []).push(d);
      return acc;
    }, {})
  );

  const onView = async (doc) => {
    try { await openXeroPdf(kind, doc.xero_id); }
    catch { setError("Couldn't open the PDF."); }
  };

  const doVoid = async () => {
    const doc = confirm;
    setConfirm(null);
    setBusyId(doc.xero_id);
    setError(null);
    try {
      const res = await voidXeroDoc(kind, doc.xero_id);
      if (res?.ok === false) setError(res.error || "Void failed.");
      else if (refetch) await refetch();
    } catch (e) {
      setError(e?.response?.data?.error || "Void failed.");
    } finally {
      setBusyId(null);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try { await refreshXero(kind, year, month); if (refetch) await refetch(); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="xero-section">
      <div className="xero-section__head">
        <h2 className="acct-stock__title">
          {title}
          {connected && items.length > 0 && <span className="xero-count">{items.length}</span>}
        </h2>
        <button type="button" className="xero-btn" onClick={onRefresh} disabled={refreshing}>
          {refreshing ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && <div className="xero-error">{error}</div>}

      {isLoading ? (
        <Loading />
      ) : !connected ? (
        <div className="deal-widget__empty">Couldn’t reach Xero. Try Refresh.</div>
      ) : items.length === 0 ? (
        <div className="deal-widget__empty">No {title.toLowerCase()} in this period.</div>
      ) : (
        <>
          <div className="xero-list">
            {main.map((doc) => (
              <DocRow key={doc.xero_id} doc={doc} kind={kind} flagLabel={flagLabel}
                onView={onView} onVoidClick={setConfirm} busy={busyId === doc.xero_id} canVoid={canVoid} />
            ))}
          </div>

          {dupes.length > 0 && (
            <div className="xero-dupes">
              <h3 className="xero-dupes__title">Potential Duplicates <span className="xero-count xero-count--dup">{dupes.length}</span></h3>
              {dupeGroups.map((group) => (
                <div className="xero-dupes__group" key={group[0].dup_key}>
                  <div className="xero-dupes__group-label">
                    {group[0].product}
                    {group[0].contact ? ` · ${group[0].contact}` : ""} · {group.length} docs
                  </div>
                  <div className="xero-list">
                    {group.map((doc) => (
                      <DocRow key={doc.xero_id} doc={doc} kind={kind} flagLabel={flagLabel}
                        onView={onView} onVoidClick={setConfirm} busy={busyId === doc.xero_id} canVoid={canVoid} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {confirm && (
        <div className="xero-modal" role="dialog" aria-modal="true">
          <div className="xero-modal__box">
            <h4 className="xero-modal__title">Void on Xero?</h4>
            <p className="xero-modal__body">
              This will {kind === "po" ? "delete the Purchase Order" : "void the Invoice"} <b>{confirm.number || confirm.xero_id}</b> on Xero.
              This can’t be undone here.
            </p>
            <div className="xero-modal__actions">
              <button type="button" className="xero-btn" onClick={() => setConfirm(null)}>Cancel</button>
              <button type="button" className="xero-btn xero-btn--danger" onClick={doVoid}>Void</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default XeroDocsSection;
